import { useQuery } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { FORM_PAGES } from '../components/Form/types';
import { setFormPage } from '../reactive/actions';
import Notifier from './Notifier';
import client from './client';
import { GetAppUserOrdersPaymentActionRequired, GetUserProfile, HandleOrderPaymentAfterAction } from './queries';
import getStripe from './stripe';
import translateService from './translateService';

interface OrderPaymentAction {
  userId: string;
  title: string;
  message: string;
  type: string;
  orderId: string;
  paymentIntentClientSecret?: string;
  setupIntentClientSecret?: string;
  paymentIntentId?: string;
  paymentMethodId?: string;
}

export default async function handleOrderPaymentAction(action: OrderPaymentAction) {
  const stripe = (await getStripe())!;
  if (action.paymentIntentClientSecret) {
    const { error, paymentIntent } = await stripe.confirmCardPayment(action.paymentIntentClientSecret!, {
      payment_method: action.paymentMethodId
    });
    if (error) {
      Notifier.error({
        message: error.message
      });
      // Notifier.error({
      //   message: i18n.t('handleOrderPaymentAction.confirmError', 'Something went wrong while confirming your payment! please contact support.')
      // });
      console.error(error.message);
    } else {
      if (paymentIntent.status === 'succeeded') {
        // The payment is complete!
      }
    }
    await client.mutate({
      mutation: HandleOrderPaymentAfterAction,
      variables: {
        orderId: action.orderId,
        paymentIntentId: paymentIntent?.id,
        paymentMethodId: paymentIntent?.payment_method,
        failure: error?.message
      }
    });
    if (error) {
      throw error;
    }
  } else if (action.setupIntentClientSecret) {
    const { error, setupIntent } = await stripe.confirmCardSetup(action.setupIntentClientSecret!);
    if (error) {
      // Show error to your customer
      Notifier.error({
        message: translateService.get().t('handleOrderPaymentAction.confirmError', 'Something went wrong while confirming your payment! please contact support.')
      });
      console.error(error.message);
    } else {
      if (setupIntent.status === 'succeeded') {
        // The payment is complete!
      }
    }
    if (error) {
      throw error;
    }
  }
}

export const orderPaymentAction = async (orders: Queries.CollarAPI_GetAppUserOrdersPaymentActionRequiredOutput[], payment: any) => {
  console.log(`PAYMENT ACTIONS TO HANDLE:`, orders);
  let errors: Error[] = [];
  for (const order of orders) {
    try {
      await handleOrderPaymentAction({
        userId: order.userId,
        title: i18n.t('handleOrderPaymentAction.paymentActionRequiredTitle', 'Your payment requires action'),
        message: i18n.t('handleOrderPaymentAction.visitAppToConfirm', 'Please visit Collar app to confirm your payment.'),
        type: 'order_action',
        orderId: order.orderId,
        paymentIntentClientSecret: order.paymentIntentClientSecret!,
        setupIntentClientSecret: order.setupIntentClientSecret!,
        paymentIntentId: order.paymentIntentId!,
        paymentMethodId: order.paymentMethodId || payment?.method
      });
    } catch (e) {
      console.error(e);
      errors.push(e);
    }
  }
  return { errors };
};

export const checkOrdersForPaymentActions = async () => {
  const {
    data: { getAppUserOrdersPaymentActionRequired: orders = [] as Queries.CollarAPI_GetAppUserOrdersPaymentActionRequiredOutput[] }
  } = await client.query<{
    getAppUserOrdersPaymentActionRequired: Queries.CollarAPI_GetAppUserOrdersPaymentActionRequiredOutput[];
  }>({
    query: GetAppUserOrdersPaymentActionRequired,
    fetchPolicy: 'no-cache'
  });

  return orders;
};

export const useCheckOrdersForPaymentActions = (
  polling: number
): [
  () => Promise<any>,
  {
    orders: Queries.CollarAPI_GetAppUserOrdersPaymentActionRequiredOutput[];
    loading: boolean;
  }
] => {
  const {
    data: { getProfile: { payment } = {} as Queries.CollarAPI_AppUser } = {},
    startPolling: getProfileStartPolling,
    stopPolling: getProfileStopPolling,
    loading: getProfileLoading
  } = useQuery<{ getProfile: Queries.CollarAPI_AppUser }>(GetUserProfile, {
    fetchPolicy: 'no-cache'
  });

  const {
    data: { getAppUserOrdersPaymentActionRequired: orders = [] as Queries.CollarAPI_GetAppUserOrdersPaymentActionRequiredOutput[] } = {},
    startPolling: getAppUserOrdersPaymentActionRequiredStartPolling,
    stopPolling: getAppUserOrdersPaymentActionRequiredStopPolling,
    loading: getAppUserOrdersPaymentActionRequiredLoading
  } = useQuery<{
    getAppUserOrdersPaymentActionRequired: Queries.CollarAPI_GetAppUserOrdersPaymentActionRequiredOutput[];
  }>(GetAppUserOrdersPaymentActionRequired, { fetchPolicy: 'no-cache' });

  useEffect(() => {
    if (polling) {
      getProfileStartPolling(polling);
      getAppUserOrdersPaymentActionRequiredStartPolling(polling);
      return () => {
        getProfileStopPolling();
        getAppUserOrdersPaymentActionRequiredStopPolling();
      };
    }
  }, []);

  const handlePaymentAction = useCallback(() => {
    return orderPaymentAction(orders, payment);
  }, [orders, payment]);

  return [
    handlePaymentAction,
    {
      orders,
      loading: getProfileLoading || getAppUserOrdersPaymentActionRequiredLoading
    }
  ];
};

export const navigateRequiredOrdersForPaymentActions = async () => {
  const orders = await checkOrdersForPaymentActions();
  if (orders.length) {
    setFormPage(FORM_PAGES.BOOKING_HANDLING);
  }
  return !!orders.length;
};
